import React, { useState, useEffect, useRef } from "react";
import QuestionItem from "../components/QuestionItem";
import QuestionsAnswersArray from "../components/QuestionsAnswersArray";
import { CButton, CCollapse } from "@coreui/react";
import { ReactComponent as PlusIcon } from "../assets/images/icons/plus.svg";
import { ReactComponent as BinIcon } from "../assets/images/icons/bin.svg";
import { ReactComponent as CopyIcon } from "../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../assets/images/icons/copy.svg";
import { Tooltip } from "react-tooltip";
import { FormArray, FormBuilder } from "react-reactive-form";
import useModalWindow from "../hooks/useModalWindow";
import { toast } from "react-toastify";

const calculateInitialRelBioBlockIds = wizardForm => {
  const relBioTableControls = Object.keys(wizardForm.controls).filter(key =>
    /^qPkRelBioTable(_\d+)?$/.test(key)
  );
  if (relBioTableControls.length > 0) {
    const ids = relBioTableControls.map(key => {
      const match = key.match(/^qPkRelBioTable_(\d+)$/);
      return match ? parseInt(match[1]) : 1;
    });
    return [...new Set(ids)].sort((a, b) => a - b);
  }
  const formValues = wizardForm.value;
  if (
    formValues.qPkRelBioAnalyses &&
    Array.isArray(formValues.qPkRelBioAnalyses)
  ) {
    const len = Math.max(1, formValues.qPkRelBioAnalyses.length);
    return Array.from({ length: len }, (_, i) => i + 1);
  }
  return [1];
};

const BioavailabilityQuestions = ({ globalId, wizardForm }) => {
  const [relBioBlockIds, setRelBioBlockIds] = useState(() =>
    calculateInitialRelBioBlockIds(wizardForm)
  );
  const nextBlockIdRef = useRef(Math.max(...relBioBlockIds, 0) + 1);
  const lastBlockRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const { modalWindow } = useModalWindow();
  const [openBlockIds, setOpenBlockIds] = useState(
    new Set([relBioBlockIds[relBioBlockIds.length - 1]])
  );

  const dash = "";

  const relBioControlsInitializedRef = useRef(new Set());
  const prevRelBioEnabledRef = useRef(wizardForm.controls.qPkRelBio?.value);
  const prevRelBioBlockIdsRef = useRef(relBioBlockIds.join(","));

  // Initialize RelBio block controls when qPkRelBio is enabled
  useEffect(() => {
    const relBioEnabled = wizardForm.controls.qPkRelBio?.value === "true";
    const relBioBlockIdsKey = relBioBlockIds.join(",");
    const relBioEnabledChanged = prevRelBioEnabledRef.current !== relBioEnabled;
    const relBioBlockIdsChanged =
      prevRelBioBlockIdsRef.current !== relBioBlockIdsKey;

    if (!relBioEnabledChanged && !relBioBlockIdsChanged) {
      return;
    }

    prevRelBioEnabledRef.current = relBioEnabled;
    prevRelBioBlockIdsRef.current = relBioBlockIdsKey;

    if (relBioEnabled) {
      relBioBlockIds.forEach(blockId => {
        const suffix = blockId > 1 ? `_${blockId}` : "";
        const blockKey = `relbio-${blockId}`;

        if (
          relBioControlsInitializedRef.current.has(blockKey) &&
          !relBioBlockIdsChanged
        ) {
          return;
        }

        const controlsToAdd = [
          {
            name: `qPkRelBioTable${suffix}`,
            factory: () => FormBuilder.control([]),
          },
          {
            name: `qPkRelBioComparisonPair${suffix}`,
            factory: () =>
              new FormArray([
                FormBuilder.group({ Test: [""], Reference: [""] }),
              ]),
          },
          {
            name: `qPkRelBioTimelines${suffix}`,
            factory: () =>
              new FormArray([FormBuilder.group({ valueHelper: [""] })]),
          },
          {
            name: `qPkRelBioEstimateRange${suffix}`,
            factory: () =>
              new FormArray([FormBuilder.group({ min: [""], max: [""] })]),
          },
        ];

        controlsToAdd.forEach(({ name, factory }) => {
          if (!wizardForm.contains(name)) {
            wizardForm.addControl(name, factory());
          }
        });

        relBioControlsInitializedRef.current.add(blockKey);
      });

      // Clean up keys for blocks that no longer exist
      const currentKeys = new Set(relBioBlockIds.map(id => `relbio-${id}`));
      relBioControlsInitializedRef.current.forEach(key => {
        if (!currentKeys.has(key)) {
          relBioControlsInitializedRef.current.delete(key);
        }
      });
    } else {
      relBioControlsInitializedRef.current.clear();
    }
  }, [wizardForm.controls.qPkRelBio?.value, relBioBlockIds]);

  // Sync block IDs when form is restored with saved RelBio blocks
  useEffect(() => {
    const newIds = calculateInitialRelBioBlockIds(wizardForm);
    const currentKey = relBioBlockIds.join(",");
    const newKey = newIds.join(",");
    if (newKey !== currentKey) {
      setRelBioBlockIds(newIds);
      nextBlockIdRef.current = Math.max(...newIds, 0) + 1;
    }
  }, [wizardForm.controls?.qPkRelBio?.value]);

  useEffect(() => {
    if (shouldScrollRef.current && lastBlockRef.current) {
      const el = lastBlockRef.current;
      let wrapper = el.parentElement;
      while (
        wrapper &&
        !wrapper.classList.contains("dashboard-content__wrapper")
      ) {
        wrapper = wrapper.parentElement;
      }
      if (wrapper) {
        const elTop = el.getBoundingClientRect().top;
        const wrapperTop = wrapper.getBoundingClientRect().top;
        wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
      }
      shouldScrollRef.current = false;
    }
  }, [relBioBlockIds.length]);

  const addRelBioPkBlock = e => {
    if (e) e.preventDefault();
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    const suffix = newId > 1 ? `_${newId}` : "";

    const controlsToEnsure = [
      {
        name: `qPkRelBioTable${suffix}`,
        factory: () => FormBuilder.control(""),
      },
      {
        name: `qPkRelBioComparisonPair${suffix}`,
        factory: () =>
          new FormArray([FormBuilder.group({ Test: [""], Reference: [""] })]),
      },
      {
        name: `qPkRelBioTimelines${suffix}`,
        factory: () =>
          new FormArray([FormBuilder.group({ valueHelper: [""] })]),
      },
      {
        name: `qPkRelBioEstimateRange${suffix}`,
        factory: () =>
          new FormArray([FormBuilder.group({ min: [""], max: [""] })]),
      },
    ];

    controlsToEnsure.forEach(({ name, factory }) => {
      if (!wizardForm.contains(name)) {
        wizardForm.addControl(name, factory());
      } else if (
        wizardForm.get(name) instanceof FormArray &&
        wizardForm.get(name).length > 0
      ) {
        while (wizardForm.get(name).length > 0) {
          wizardForm.get(name).removeAt(0);
        }
      }
    });

    shouldScrollRef.current = true;
    setRelBioBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
  };

  const removeRelBioPkBlock = blockId => {
    const suffix = blockId > 1 ? `_${blockId}` : "";
    const controlsToRemove = [
      `qPkRelBioTable${suffix}`,
      `qPkRelBioComparisonPair${suffix}`,
      `qPkRelBioTimelines${suffix}`,
      `qPkRelBioEstimateRange${suffix}`,
    ];

    controlsToRemove.forEach(name => {
      if (wizardForm.contains(name)) {
        wizardForm.removeControl(name);
      }
    });
  };

  const handleRemoveBlock = async (e, displayIndex) => {
    if (e) e.preventDefault();
    if (relBioBlockIds.length <= 1) return;
    const confirmed = await modalWindow({
      title: "Remove Block",
      message: "Do you want to remove this block of questions?",
      confirmButtonLabel: "Yes",
    });
    if (!confirmed) return;
    const blockId = relBioBlockIds[displayIndex];
    removeRelBioPkBlock(blockId);
    const remainingIds = relBioBlockIds.filter((_, i) => i !== displayIndex);
    setRelBioBlockIds(remainingIds);
    setOpenBlockIds(new Set([remainingIds[remainingIds.length - 1]]));
  };

  const duplicateRelBioBlock = (sourceBlockId, newBlockId) => {
    const sourceSuffix = sourceBlockId > 1 ? `_${sourceBlockId}` : "";
    const newSuffix = newBlockId > 1 ? `_${newBlockId}` : "";

    const controlsToCreate = [
      {
        name: `qPkRelBioTable${newSuffix}`,
        factory: () => FormBuilder.control(""),
      },
      {
        name: `qPkRelBioComparisonPair${newSuffix}`,
        factory: () =>
          new FormArray([FormBuilder.group({ Test: [""], Reference: [""] })]),
      },
      {
        name: `qPkRelBioTimelines${newSuffix}`,
        factory: () =>
          new FormArray([FormBuilder.group({ valueHelper: [""] })]),
      },
      {
        name: `qPkRelBioEstimateRange${newSuffix}`,
        factory: () =>
          new FormArray([FormBuilder.group({ min: [""], max: [""] })]),
      },
    ];

    controlsToCreate.forEach(({ name, factory }) => {
      if (!wizardForm.contains(name)) {
        wizardForm.addControl(name, factory());
      } else {
        const ctrl = wizardForm.get(name);
        if (ctrl instanceof FormArray) {
          while (ctrl.length > 0) ctrl.removeAt(0);
        }
      }
    });

    const tableSource = wizardForm.get(`qPkRelBioTable${sourceSuffix}`);
    const tableNew = wizardForm.get(`qPkRelBioTable${newSuffix}`);
    if (tableSource && tableNew) {
      tableNew.setValue(tableSource.value);
    }

    const formArrayControls = [
      "qPkRelBioComparisonPair",
      "qPkRelBioTimelines",
      "qPkRelBioEstimateRange",
    ];

    formArrayControls.forEach(baseName => {
      const sourceArray = wizardForm.get(`${baseName}${sourceSuffix}`);
      const newArray = wizardForm.get(`${baseName}${newSuffix}`);
      if (!(sourceArray instanceof FormArray)) return;
      if (!(newArray instanceof FormArray)) return;
      while (newArray.length > 0) newArray.removeAt(0);
      sourceArray.controls.forEach((sourceGroup, index) => {
        const sourceValue = sourceGroup.value || {};
        const groupConfig = Object.keys(sourceValue).reduce((acc, key) => {
          acc[key] = [sourceValue[key] ?? ""];
          return acc;
        }, {});
        const newGroup = FormBuilder.group(groupConfig);
        newGroup.meta = { key: Date.now() + index };
        newArray.push(newGroup);
      });
    });
  };

  const handleDuplicateBlock = (e, displayIndex) => {
    if (e) e.preventDefault();
    const sourceBlockId = relBioBlockIds[displayIndex];
    if (sourceBlockId === undefined) return;
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    duplicateRelBioBlock(sourceBlockId, newId);
    shouldScrollRef.current = true;
    setRelBioBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
    toast.success("Block duplicated successfully");
  };

  const handleDuplicateLastBlock = e => {
    if (e) e.preventDefault();
    if (relBioBlockIds.length === 0) return;
    handleDuplicateBlock(e, relBioBlockIds.length - 1);
  };

  const isPkEnabled = true;

  const renderBlockContent = (blockId, displayIndex) => {
    const suffix = blockId > 1 ? `_${blockId}` : "";
    const isFirstBlock = displayIndex === 0;
    const blockNumber = displayIndex + 1;

    const questionIndex1a = isFirstBlock ? "2" : `2${dash}(${blockNumber})`;
    const questionIndex1b = isFirstBlock ? "3" : `3${dash}(${blockNumber})`;
    const questionIndex1c = isFirstBlock ? "4" : `4${dash}(${blockNumber})`;
    const questionIndex1d = isFirstBlock ? "5" : `5${dash}(${blockNumber})`;

    return (
      <>
        {isPkEnabled && wizardForm.controls.qPkRelBio?.value === "true" && (
          <div
            style={{
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
            }}>
            <CButton
              color={"primary"}
              shape={"pill"}
              data-tooltip-id={`duplicate-relbio-block-${blockId}`}
              style={{
                border: "1px solid #006dee",
                background: "#006dee",
                fontSize: "15px",
                color: "#fff",
                boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
              }}
              onClick={e => handleDuplicateBlock(e, displayIndex)}>
              <CopyIcon /> Duplicate
            </CButton>
            <Tooltip
              id={`duplicate-relbio-block-${blockId}`}
              place="top"
              delayShow={100}
              className="custom-tooltip">
              Duplicate this block
            </Tooltip>
            {!isFirstBlock && (
              <>
                <CButton
                  color={"danger"}
                  shape={"pill"}
                  data-tooltip-id={`remove-relbio-block-${blockId}`}
                  onClick={e => handleRemoveBlock(e, displayIndex)}>
                  <BinIcon />
                </CButton>
                <Tooltip
                  id={`remove-relbio-block-${blockId}`}
                  place="top"
                  delayShow={100}
                  className="custom-tooltip">
                  Remove this block
                </Tooltip>
              </>
            )}
          </div>
        )}

        <QuestionItem
          questionIndex={questionIndex1a}
          questionName={`qPkRelBioTable${suffix}`}
          questionsName={"qPkRelBioTable"}
          suffix={suffix}
          activator={
            wizardForm.controls.qPkRelBio?.value === "true" && isPkEnabled
          }
          formGroup={wizardForm}
          isPlainSingleSelectDropdown={true}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1b}
          controlName={`qPkRelBioComparisonPair${suffix}`}
          questionsName={"qPkRelBioComparisonPair"}
          activator={
            wizardForm.controls.qPkRelBio?.value === "true" && isPkEnabled
          }
          formGroup={wizardForm}
          minInputs={1}
          maxInputs={1}
          inputTextBoxes={[
            ["Test", "Test", "richtext"],
            ["Reference", "Reference", "richtext"],
          ]}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1c}
          controlName={`qPkRelBioTimelines${suffix}`}
          questionsName={"qPkRelBioTimelines"}
          activator={
            wizardForm.controls.qPkRelBio?.value === "true" && isPkEnabled
          }
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "text"]]}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1d}
          controlName={`qPkRelBioEstimateRange${suffix}`}
          questionsName={"qPkRelBioEstimateRange"}
          activator={
            wizardForm.controls.qPkRelBio?.value === "true" && isPkEnabled
          }
          formGroup={wizardForm}
          minInputs={1}
          maxInputs={1}
          inputTextBoxes={[
            ["Lower bound", "min", "text", "Enter numeric value (e.g., 1.1)"],
            ["Upper bound", "max", "text", "Enter numeric value (e.g., 1.1)"],
          ]}
          globalId={globalId}
        />
      </>
    );
  };

  return (
    <>
      {/* Is Relative Bioavailability analysed? */}
      <QuestionItem
        questionIndex={"1"}
        questionName={"qPkRelBio"}
        activator={isPkEnabled}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />

      {/* Relative Bioavailability Analysis Blocks */}
      {wizardForm.controls.qPkRelBio?.value === "true" && (
        <>
          {relBioBlockIds.length === 1 ? (
            <div
              ref={lastBlockRef}
              className="analysis-block"
              //style={{ marginBottom: "2rem" }}
            >
              {renderBlockContent(relBioBlockIds[0], 0)}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                }}>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set(relBioBlockIds))}>
                  Expand all
                </CButton>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set())}>
                  Collapse all
                </CButton>
              </div>
              <div className="accordion">
                {relBioBlockIds.map((blockId, displayIndex) => {
                  const isOpen = openBlockIds.has(blockId);
                  return (
                    <div
                      key={`pk-relbio-block-${blockId}`}
                      className="accordion-item"
                      ref={
                        displayIndex === relBioBlockIds.length - 1
                          ? lastBlockRef
                          : null
                      }>
                      <div className="accordion-header">
                        <button
                          type="button"
                          className={`accordion-button${isOpen ? "" : " collapsed"}`}
                          onClick={() =>
                            setOpenBlockIds(prev => {
                              const next = new Set(prev);
                              next.has(blockId)
                                ? next.delete(blockId)
                                : next.add(blockId);
                              return next;
                            })
                          }>
                          Block {displayIndex + 1}
                        </button>
                      </div>
                      <CCollapse
                        visible={isOpen}
                        className="accordion-collapse">
                        <div className="accordion-body">
                          <div
                            className="analysis-block"
                            //style={{ marginBottom: "2rem" }}
                          >
                            {renderBlockContent(blockId, displayIndex)}
                          </div>
                        </div>
                      </CCollapse>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {isPkEnabled && (
            <div
              style={{
                marginTop: "1rem",
                marginBottom: "2rem",
                display: "flex",
                gap: "0.5rem",
              }}>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={addRelBioPkBlock}>
                Add another Relative Bioavailability Analysis <PlusIcon />
              </CButton>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleDuplicateLastBlock}>
                Duplicate block above <CopyOutlineIcon />
              </CButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default BioavailabilityQuestions;
